import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { authService } from '../services/authService';
import './Auth.css';

const ActivateAccount: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!token) {
      setError('Verification token was not found.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setLoading(true);
      const response = await authService.activateAccount(token, password);
      setSuccess(response.message || 'Account activated.');
      setPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Account activation failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card-wrapper">
        <Card className="auth-card">
          <div className="auth-header">
            <img
              src="/UptimioHelpdesk.png"
              alt="Uptimio Helpdesk"
              className="auth-logo-image"
              style={{ height: '80px', width: 'auto', marginBottom: '1rem' }}
            />
            <div className="auth-subtitle">Activate Account</div>
          </div>

          {error && <Message severity="error" text={error} className="w-full" style={{ marginBottom: '1rem' }} />}
          {success && <Message severity="success" text={success} className="w-full" style={{ marginBottom: '1rem' }} />}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="p-field">
              <label htmlFor="password">
                <i className="pi pi-lock" style={{ marginRight: '0.5rem', color: '#10b981' }}></i>
                New Password
              </label>
              <InputText
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                className="w-full"
                required
              />
            </div>

            <div className="p-field">
              <label htmlFor="confirmPassword">
                <i className="pi pi-check-circle" style={{ marginRight: '0.5rem', color: '#10b981' }}></i>
                Confirm Password
              </label>
              <InputText
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat password"
                className="w-full"
                required
              />
            </div>

            <Button
              label={loading ? 'Activating...' : 'Verify email and set password'}
              icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
              type="submit"
              className="w-full"
              loading={loading}
              disabled={loading || !!success}
            />

            {success && (
              <Button
                type="button"
                label="Go to login"
                icon="pi pi-sign-in"
                className="p-button-secondary w-full"
                onClick={() => navigate('/login')}
              />
            )}
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ActivateAccount;
